import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import { IconButton, Modal, SecondaryActionButton, StonePanel } from '@aztec-realms/ui';
import { useQueryClient } from '@tanstack/react-query';

import {
  inboxActionPath,
  inboxReceiptId,
  ownerInboxKey,
  useEarnedInbox,
  useEarnedInboxItem,
  useReadInboxItem,
  type InboxItem,
} from './data/earned-inbox-api';
import './earned-inbox.css';
import { PushValuePrompt } from './PushValuePrompt';

interface Props {
  ownerId: string;
  membershipScope: string;
  passive: boolean;
}

/** Remount on authority/safety changes: discard local state and old requests together. */
export function EarnedInbox(props: Props) {
  return props.passive ? (
    <span className="earned-inbox-passive">Inbox · when stopped</span>
  ) : (
    <InboxContent key={`${props.ownerId}:${props.membershipScope}`} {...props} />
  );
}

function InboxContent({ ownerId, membershipScope }: Props) {
  const scope = `${ownerId}:${membershipScope}`;
  const client = useQueryClient();
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const receiptId = inboxReceiptId(location.pathname, location.search);
  const invalidReceipt =
    location.pathname === '/tribe' &&
    new URLSearchParams(location.search).has('inbox') &&
    !receiptId;
  const detailOpen = Boolean(receiptId || invalidReceipt);
  const inbox = useEarnedInbox(scope, true);
  const detail = useEarnedInboxItem(scope, receiptId, detailOpen);
  const read = useReadInboxItem(scope);
  const unread = inbox.isError ? undefined : inbox.data?.pages[0]?.unread_count;
  const items = inbox.data?.pages.flatMap((page) => page.items) ?? [];
  // Remove private projections when the owner/membership or safety boundary changes.
  useEffect(
    () => () => {
      void client.cancelQueries({ queryKey: ownerInboxKey(scope) });
      client.removeQueries({ queryKey: ownerInboxKey(scope) });
    },
    [client, scope],
  );

  const close = () => {
    setOpen(false);
    read.reset();
    if (detailOpen) {
      const search = new URLSearchParams(location.search);
      search.delete('inbox');
      navigate({ pathname: '/tribe', search: search.toString() }, { replace: true });
    }
  };
  const openInbox = () => {
    setOpen(true);
    read.reset();
    void inbox.refetch();
  };
  const renderItem = (item: InboxItem, selected = false) => (
    <StonePanel
      className="earned-inbox__receipt"
      data-inbox-receipt={item.id}
      data-selected={selected || undefined}
      key={item.id}
    >
      <span className="earned-inbox__state">{item.read_at ? 'Read' : 'Unread'}</span>
      <h3>{item.title}</h3>
      <p>{item.body}</p>
      <time dateTime={item.created_at}>
        {new Date(item.created_at).toLocaleString(undefined, {
          month: 'short',
          day: 'numeric',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        })}
      </time>
      <div className="earned-inbox__actions">
        {!selected && inboxActionPath(item) && (
          <SecondaryActionButton
            onClick={() => {
              setOpen(false);
              read.reset();
              navigate(inboxActionPath(item)!);
            }}
          >
            {item.action_label ?? 'View thank-you'}
          </SecondaryActionButton>
        )}
        {!item.read_at && (
          <SecondaryActionButton disabled={read.isPending} onClick={() => read.mutate(item.id)}>
            {read.isPending && read.variables === item.id ? 'Saving read status…' : 'Mark as read'}
          </SecondaryActionButton>
        )}
      </div>
      {!item.context_available && (
        <p className="earned-inbox__muted">
          The original shared activity is no longer available. This receipt remains in your inbox.
        </p>
      )}
    </StonePanel>
  );

  return (
    <>
      <IconButton
        className="earned-inbox-entry"
        label={`Open inbox${unread === undefined ? '' : `, ${unread} unread`}`}
        onClick={openInbox}
      >
        <span>Inbox</span>
        {Boolean(unread) && (
          <span className="earned-inbox-entry__count" aria-hidden="true">
            {unread! > 99 ? '99+' : unread}
          </span>
        )}
      </IconButton>
      {(open || detailOpen) &&
        createPortal(
          <div className="earned-inbox-overlay">
            <Modal open title={detailOpen ? 'Your thank-you' : 'Your inbox'} onClose={close}>
              <div className="earned-inbox__content">
                {detailOpen ? (
                  <>
                    {detail.isPending && !invalidReceipt && (
                      <p role="status">Loading your thank-you…</p>
                    )}
                    {(detail.isError || invalidReceipt) && (
                      <div role="alert">
                        <h3>This thank-you is unavailable</h3>
                        <p>
                          It may have been removed or may not belong to this account. Your inbox has
                          the available receipts.
                        </p>
                        {!invalidReceipt && (
                          <SecondaryActionButton onClick={() => void detail.refetch()}>
                            Try again
                          </SecondaryActionButton>
                        )}
                      </div>
                    )}
                    {!detail.isError &&
                      detail.isFetchedAfterMount &&
                      detail.data &&
                      renderItem(detail.data, true)}
                  </>
                ) : (
                  <>
                    <p>Real thanks for the help you gave. No reply is expected.</p>
                    {inbox.isPending && <p role="status">Checking your inbox…</p>}
                    {inbox.isError ? (
                      <div role="alert">
                        <h3>Your inbox is unavailable</h3>
                        <p>We couldn’t confirm your current receipts. Try again when connected.</p>
                        <SecondaryActionButton onClick={() => void inbox.refetch()}>
                          Try again
                        </SecondaryActionButton>
                      </div>
                    ) : (
                      <>
                        {inbox.isSuccess && items.length === 0 && (
                          <p role="status">
                            No thank-yous yet. When another player thanks you for a real
                            contribution or trade, it will appear here.
                          </p>
                        )}
                        <div className="earned-inbox__items">
                          {items.map((item) => renderItem(item))}
                        </div>
                        {inbox.hasNextPage && (
                          <SecondaryActionButton
                            disabled={inbox.isFetching}
                            onClick={() => void inbox.fetchNextPage()}
                          >
                            {inbox.isFetchingNextPage
                              ? 'Loading earlier thanks…'
                              : 'Earlier thanks'}
                          </SecondaryActionButton>
                        )}
                      </>
                    )}
                  </>
                )}
                {read.isError && (
                  <div role="alert">
                    <p>Read status could not be saved. Your thank-you is still available.</p>
                    <SecondaryActionButton
                      onClick={() => read.variables && read.mutate(read.variables)}
                    >
                      Retry read status
                    </SecondaryActionButton>
                  </div>
                )}
                {read.isSuccess && <p role="status">Read status saved.</p>}
                {((detailOpen && !detail.isError && detail.isFetchedAfterMount && detail.data) ||
                  (!detailOpen && inbox.isSuccess && items.length > 0)) && (
                  <PushValuePrompt
                    ownerId={ownerId}
                    onReview={() => {
                      close();
                      navigate('/settings');
                    }}
                  />
                )}
              </div>
              <footer className="earned-inbox__footer">
                {detailOpen && (
                  <SecondaryActionButton
                    onClick={() => {
                      close();
                      openInbox();
                    }}
                  >
                    Back to inbox
                  </SecondaryActionButton>
                )}
                <SecondaryActionButton onClick={close}>Return to the realm</SecondaryActionButton>
              </footer>
            </Modal>
          </div>,
          document.querySelector('.ar-game-shell') ?? document.body,
        )}
    </>
  );
}
